import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { notificationsService } from '../services/notificationsService';

/**
 * NotificationDetailModal - Modal para exibir conteudo rico de uma notificacao
 * @param {Object} props
 * @param {boolean} props.open - Se o modal esta aberto
 * @param {function} props.onClose - Callback ao fechar
 * @param {string|null} props.notificationId - ID da notificacao para buscar via API
 * @param {Object|null} props.notification - Notificacao ja carregada (evita busca)
 */
export default function NotificationDetailModal({ open, onClose, notificationId }) {
  const [notification, setNotification] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && notificationId) {
      setLoading(true);
      notificationsService.getNotificationById(notificationId)
        .then(data => {
          setNotification(data);
          // Mark as read if unread
          if (data && !data.readAt) {
            notificationsService.markAsRead(notificationId);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [open, notificationId]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setNotification(null);
    }
  }, [open]);

  const handleActionClick = () => {
    const url = notification?.actionUrl || notification?.data?.actionUrl;
    if (url) {
      if (url.startsWith('/')) {
        window.location.hash = '';
        window.location.pathname = url;
      } else {
        window.open(url, '_blank');
      }
    }
    onClose();
  };

  const actionUrl = notification?.actionUrl || notification?.data?.actionUrl;
  const bodyHtml = notification?.body;
  const sanitizedBody = bodyHtml ? DOMPurify.sanitize(bodyHtml, {
    ALLOWED_TAGS: ['b', 'i', 'u', 'strong', 'em', 'br', 'p', 'ul', 'ol', 'li', 'a', 'img', 'span', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'font', 'sub', 'sup'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'width', 'height', 'style', 'target', 'rel', 'class', 'color', 'face', 'size', 'colspan', 'rowspan']
  }) : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      {loading ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </Box>
      ) : !notification ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Notificacao nao encontrada</Typography>
          <Button onClick={onClose} sx={{ mt: 2 }}>Fechar</Button>
        </Box>
      ) : (
        <>
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
              {notification.title || 'Notificacao'}
            </Typography>
            {(notification.summary || notification.message) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {notification.summary || notification.message}
              </Typography>
            )}
          </DialogTitle>

          <Divider />

          <DialogContent sx={{ pt: 2 }}>
            {/* Rich HTML body */}
            {sanitizedBody && (
              <Box
                sx={{
                  '& p': { mt: 0, mb: 1 },
                  '& a': { color: 'primary.main' },
                  '& ul, & ol': { pl: 2 },
                  '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}
                dangerouslySetInnerHTML={{ __html: sanitizedBody }}
              />
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            {actionUrl && (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                onClick={handleActionClick}
              >
                Ir para
              </Button>
            )}
            <Button
              variant={actionUrl ? 'outlined' : 'contained'}
              startIcon={<CloseIcon />}
              onClick={onClose}
            >
              Fechar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
